/**
 * Which product each top-level route belongs to, and what that means for
 * where an organization may go (#11422 round 17). The product list is the
 * one the sign-in answer carries (`organization.allowed_products`); the
 * frontend never learns which client an organization is.
 */

/** The documents product's own page. */
const DOCUMENTS_PATH = "/documents";

/**
 * Where the intake queue lives (#11422 round 19, R8), and the one place its
 * path is named: the queue is `INTAKE_PATH`, a document is
 * `INTAKE_PATH/<id>`. It is the documents product's path: a deployment that
 * reviews PHI documents (`usePhiDocumentReview`) shows the queue there, every
 * other deployment the documents product (`MainApp/index.jsx`).
 */
// The daily inventory (#11422), under the queue.
export const INTAKE_TODAY_PATH = "/documents/today";
export const INTAKE_PATH = DOCUMENTS_PATH;

/** The queue's address before round 19. Old links and bookmarks under it
 *  are sent on to `INTAKE_PATH`, with the document id and the query. */
export const LEGACY_INTAKE_PATH = "/claims";

/** The queue's address, with an optional `?query`. */
export const intakeQueuePath = (search: string = ""): string =>
  `${INTAKE_PATH}${search}`;

/** A document's review page, with an optional `?query`. */
export const intakeDocumentPath = (
  itemId: string,
  search: string = ""
): string => `${INTAKE_PATH}/${itemId}${search}`;

/**
 * Top-level routes, each with the product that owns it, in landing order:
 * "/" goes to the first one the organization has. The claims queue leads, so
 * a claims-only organization always lands there; chat comes next, as it
 * always has for everyone else. Artifacts is last because every organization
 * on a full deployment carries it, so it says nothing about what the
 * organization came for.
 */
const PRODUCT_ROUTES: ReadonlyArray<{ path: string; product: string }> = [
  { path: INTAKE_PATH, product: "bcbswy_intake" },
  { path: "/chat", product: "chat" },
  { path: "/rms", product: "crm" },
  { path: DOCUMENTS_PATH, product: "manage_documents" },
  { path: "/matrix", product: "querygrid" },
  { path: "/reports", product: "report_generation" },
  { path: "/notetaker", product: "notetaker" },
  { path: "/alerts", product: "alerts" },
  { path: "/exposures", product: "exposures" },
  { path: "/tasks", product: "tasks_full" },
  { path: "/agents", product: "agents" },
  { path: "/artifacts", product: "artifacts" },
];

const SETTINGS_PATH = "/settings";

const under = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

/** Whether `pathname` is the intake queue or one of its documents, on a
 *  deployment that does (`phiDocumentReview`) or does not review PHI
 *  documents. */
export const isIntakeRoute = (
  pathname: string,
  phiDocumentReview: boolean
): boolean => phiDocumentReview && under(pathname, INTAKE_PATH);

/** Whether `pathname` is the documents product's own page, which handles
 *  its own `?docId=` deep link and entity previews. On a deployment that
 *  reviews PHI documents the same path is the intake queue, which does not,
 *  so the shell's viewer handles them there as on any other page. */
export const isDocumentsProductPage = (
  pathname: string,
  phiDocumentReview: boolean
): boolean => !phiDocumentReview && pathname === DOCUMENTS_PATH;

/** Whether `pathname` is one document's address under the queue (the review
 *  page), not the queue itself. */
export const isIntakeDocumentPath = (pathname: string): boolean =>
  pathname.startsWith(`${INTAKE_PATH}/`) &&
  pathname.length > INTAKE_PATH.length + 1;

/**
 * An organization with the claims product and no chat. Its claim files are
 * kept out of the filesystem, so the rest of the product has nothing of its
 * work, and on the document-intake deployment most of it is not registered.
 */
export const isClaimsOnly = (products: readonly string[]): boolean =>
  products.includes("bcbswy_intake") && !products.includes("chat");

/**
 * Where "/" lands: the first route in landing order whose product the
 * organization has (the claims queue for a claims-only organization, chat
 * for one that has chat), and Settings when it has none.
 */
export const homePath = (products: readonly string[]): string =>
  PRODUCT_ROUTES.find((r) => products.includes(r.product))?.path ??
  SETTINGS_PATH;

/**
 * Where a claims-only organization is sent from `pathname`, or null when it
 * may stay. It keeps Settings, the routes of the products it has, and the
 * queue's old address, whose own redirect keeps the document id; every
 * other route goes to the claims queue. Any other organization is never sent
 * anywhere.
 */
export const claimsOnlyRedirect = (
  pathname: string,
  products: readonly string[]
): string | null => {
  if (!isClaimsOnly(products)) return null;
  if (under(pathname, SETTINGS_PATH)) return null;
  if (under(pathname, LEGACY_INTAKE_PATH)) return null;
  const owned = PRODUCT_ROUTES.some(
    (r) => under(pathname, r.path) && products.includes(r.product)
  );
  return owned ? null : INTAKE_PATH;
};
